import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import Shimmer from "./ui/Shimmer";
import { usePermissions } from "../context/PermissionContext";
import SortIcons from "./ui/SortIcons";
import { apiCall } from "../Apis/apiCall";
import { NumberPlateGenerator } from "./ui/NumberPlateGenerator";
import ErrorAlert from "./ui/ErrorAlert";

const PlateAuthortiesLayer = () => {
  const [authorities, setAuthorities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [authorityId, setAuthorityId] = useState(null);
  const [authorityName, setAuthorityName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedAuthority, setSelectedAuthority] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: "authority_name",
    direction: "asc",
  });
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const fetchAuthorities = useCallback(
    async (
      page = 1,
      perPage = 10,
      search = "",
      sortBy = "authority_name",
      sortOrder = "asc"
    ) => {
      setLoading(true);
      setError(null);
      try {
        console.log("🔄 Fetching authorities with params:", {
          page,
          perPage,
          search,
          sortBy,
          sortOrder,
        });

        const response = await apiCall(
          "get",
          "/get-all-plate-authorities",
          {
            page,
            per_page: perPage,
            search,
            sort_by: sortBy,
            sort_order: sortOrder,
          },
          navigate
        );

        // Debug logs - ye dekho console mein kya aa raha hai
        console.log("✅ API Response:", response.data);
        console.log("📄 Current Page Requested:", page);
        console.log("📊 Data received:", response.data.data?.length, "records");
        console.log("🔢 Pagination Info:", response.data.pagination);

        const mappedAuthorities = response.data.data.map((authority) => ({
          authority_id: authority.authority_id,
          authority_name: authority.authority_name,
          country_name: authority.country_name,
          created_at: authority.created_at,
          is_active: authority.is_active,
          format_pattern: authority.format_pattern,
          color_hex: authority.color_hex,
          text_color_hex: authority.text_color_hex,
          border_color_hex: authority.border_color_hex,
          emblem: authority.emblem,
          font_name: authority.font_name,
          fields: authority.fields,
        }));

        console.log("✨ Mapped Authorities:", mappedAuthorities);

        setAuthorities(mappedAuthorities || []);

        // Pagination state set karo - API returns last_page, not totalPages
        setTotalEntries(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.last_page || 1);
        setCurrentPage(response.data.pagination?.current_page || page);

        console.log(
          "📌 State Updated - Total:",
          response.data.pagination?.total,
          "Pages:",
          response.data.pagination?.last_page
        );
      } catch (error) {
        console.error("❌ Error fetching authorities:", error);
        console.error("❌ Error details:", error?.response?.data);

        if (sortBy === "created_at" && error?.message?.includes("ambiguous")) {
          console.log("⚠️ Retrying with authority_name sort...");
          return fetchAuthorities(
            page,
            perPage,
            search,
            "authority_name",
            sortOrder
          );
        }

        setAuthorities([]);
        setTotalEntries(0);
        setTotalPages(1);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1);
      fetchAuthorities(
        1,
        perPage,
        query,
        sortConfig.field,
        sortConfig.direction
      );
    }, 500),
    [perPage, sortConfig.field, sortConfig.direction, fetchAuthorities]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    fetchAuthorities(currentPage, perPage, searchQuery, field, newDirection);
  };

  const sortedAuthorities = useMemo(() => {
    return [...authorities].sort((a, b) => {
      if (!sortConfig.field) return 0;
      const valueA = a[sortConfig.field] ?? "";
      const valueB = b[sortConfig.field] ?? "";
      if (sortConfig.field === "is_active") {
        const valA = valueA ? "Active" : "Inactive";
        const valB = valueB ? "Active" : "Inactive";
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return sortConfig.direction === "asc"
        ? valueA.toString().localeCompare(valueB.toString())
        : valueB.toString().localeCompare(valueA.toString());
    });
  }, [authorities, sortConfig]);

  useEffect(() => {
    fetchAuthorities(
      currentPage,
      perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    currentPage,
    perPage,
    sortConfig.field,
    sortConfig.direction,
    fetchAuthorities,
  ]);

  const handleOpenModal = (type, authority = null) => {
    setModalType(type);
    setShowModal(true);
    setAuthorityId(null);
    setAuthorityName("");
    setIsActive(true);
    setSelectedAuthority(null);

    if (authority) {
      if (!authority.authority_id || isNaN(authority.authority_id)) {
        console.error("Invalid Authority ID.");
        return;
      }
      setSelectedAuthority(authority);
      setAuthorityId(authority.authority_id);
      if (type === "update") {
        setModalLoading(true);
        setTimeout(() => {
          setAuthorityName(authority.authority_name || "");
          setIsActive(authority.is_active || false);
          setModalLoading(false);
        }, 1000);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setAuthorityId(null);
    setAuthorityName("");
    setIsActive(true);
    setSelectedAuthority(null);
    setModalLoading(false);
  };

  const handleDeleteAuthority = async () => {
    if (!authorityId || isNaN(authorityId)) {
      console.error("Cannot delete: Invalid Authority ID.");
      return;
    }

    setSaveLoading(true);
    try {
      await apiCall(
        "delete",
        `/delete-plate-authority/${authorityId}`,
        null,
        navigate
      );
      fetchAuthorities(currentPage, perPage, searchQuery);
      handleCloseModal();
    } catch (error) {
      console.error("Error deleting authority:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateAuthority = async () => {
    if (!authorityId || isNaN(authorityId)) {
      console.error("Cannot update: Invalid Authority ID.");
      return;
    }
    if (!authorityName.trim()) {
      console.error("Authority name is required.");
      return;
    }

    setSaveLoading(true);
    try {
      await apiCall(
        "put",
        `/update-plate-authority/${authorityId}`,
        {
          authority_name: authorityName,
          is_active: isActive,
        },
        navigate
      );
      fetchAuthorities(currentPage, perPage, searchQuery);
      handleCloseModal();
    } catch (error) {
      console.error("Error updating authority:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "delete") handleDeleteAuthority();
    else if (modalType === "update") handleUpdateAuthority();
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);
  if (error) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by authority name or Country"
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Search authorities"
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={(value) => {
                setPerPage(value);
                setCurrentPage(1);
              }}
            />
            <div className="ms-auto">
              {hasPermission("insert_plate_authority") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => navigate("/add-plate-authority")}
                  aria-label="Add new authority"
                >
                  <Icon icon="ri:add-line" className="me-1" /> Add Authority
                </button>
              )}
            </div>
          </div>

          {authorities.length === 0 && !loading ? (
            <TableDataNoAvailable message="No authorities found." />
          ) : (
            <div className="position-relative">
              <div
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <div className="table-responsive">
                  <table
                    className="table bordered-table mb-0 table-hover"
                    aria-label="Authorities table"
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col">S.No</th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("authority_name")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "authority_name"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Authority Name</span>
                            <SortIcons
                              field="authority_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("country_name")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "country_name"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Country</span>
                            <SortIcons
                              field="country_name"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("example")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "example"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Example</span>
                            <SortIcons
                              field="example"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("created_at")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "created_at"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="created_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="p-2"
                          onClick={() => handleSort("is_active")}
                          style={{ cursor: "pointer" }}
                          aria-sort={
                            sortConfig.field === "is_active"
                              ? sortConfig.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Status</span>
                            <SortIcons
                              field="is_active"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {authorities.map((authority, index) => (
                        <tr key={authority.authority_id || index}>
                          <td>{startEntry + index}</td>
                          <td className="p-2">
                            <span className="">
                              {authority.authority_name || "-"}
                            </span>
                          </td>
                          <td className="p-2">
                            {authority.country_name || "-"}
                          </td>
                          <td className="p-2">
                            <NumberPlateGenerator
                              config={{
                                format_pattern: authority.format_pattern,
                                color_hex: authority.color_hex,
                                text_color_hex: authority.text_color_hex,
                                border_color_hex: authority.border_color_hex,
                                emblem: authority.emblem,
                                font_name: authority.font_name,
                              }}
                            />
                          </td>
                          <td className="p-2">
                            {authority.created_at || "-"}
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                authority.is_active
                                  ? "bg-success-focus text-success-600 border border-success-main"
                                  : "bg-danger-focus text-danger-600 border border-danger-main"
                              }`}
                            >
                              {authority.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="p-2">
                            <div className="d-flex gap-2">
                              {hasPermission("edit_plate_authority") && (
                                <button
                                  className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    navigate(
                                      `/edit-plate-authority/${authority.authority_id}`
                                    )
                                  }
                                  aria-label={`Edit authority ${
                                    authority.authority_name || "-"
                                  }`}
                                >
                                  <Icon icon="lucide:edit" />
                                  <span className="tooltip">Edit</span>
                                </button>
                              )}
                              {hasPermission("delete_plate_authority") && (
                                <button
                                  className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center position-relative"
                                  onClick={() =>
                                    handleOpenModal("delete", authority)
                                  }
                                  aria-label={`Delete authority ${
                                    authority.authority_name || "-"
                                  }`}
                                >
                                  <Icon icon="mingcute:delete-2-line" />
                                  <span className="tooltip">Delete</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{ zIndex: 10, borderRadius: "10px", padding: "20px" }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{ currentPage, perPage, total: totalEntries }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modal for update and delete actions */}
      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-hidden={!showModal}
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalTitle">
                {modalType === "delete"
                  ? "Delete Authority"
                  : "Update Authority"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close modal"
              ></button>
            </div>
            <div className="modal-body">
              {modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {selectedAuthority?.authority_name || "this authority"}
                  </strong>
                  ?
                </p>
              ) : modalType === "update" && modalLoading ? (
                <Shimmer type="modal" />
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="authorityName" className="form-label">
                      Authority Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="authorityName"
                      value={authorityName}
                      onChange={(e) => setAuthorityName(e.target.value)}
                      placeholder="Enter authority name"
                      aria-describedby="authorityNameHelp"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="isActive" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="isActive"
                      value={isActive}
                      onChange={(e) => setIsActive(e.target.value === "true")}
                      aria-describedby="isActiveHelp"
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete" ? "btn-danger" : "btn-primary-600"
                }`}
                onClick={handleSubmit}
                disabled={saveLoading || modalLoading}
                aria-label={
                  modalType === "delete" ? "Confirm delete" : "Update authority"
                }
              >
                {modalType === "delete"
                  ? saveLoading
                    ? "Deleting..."
                    : "Delete Authority"
                  : saveLoading
                  ? "Updating..."
                  : "Update Authority"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .position-relative {
            position: relative;
          }
          .tooltip {
            visibility: hidden;
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background-color: #333;
            color: white;
            padding: 5px 10px;
            border-radius: 4px;
            font-size: 12px;
            white-space: nowrap;
            z-index: 1000;
            opacity: 0;
            transition: opacity 0.2s;
          }
          button:hover .tooltip {
            visibility: visible;
            opacity: 1;
          }
        `}
      </style>
    </div>
  );
};

export default PlateAuthortiesLayer;
